import 'ag-grid-enterprise';
import { IDatasource, SortModelItem } from 'ag-grid-community';
import { ONLY_MY_TRANSFER_LABEL, SERVER_PAGE_SIZE } from '@/model/Constants';
import { API_KEYWORD_SEPARATOR, client } from '@/api/backoffice-api';
import SortModelMap from '@/utils/grid/datasource/SortModelMap';
import Transfer from '@/model/transfer.mjs';
import logger from '@/utils/logger.mjs';
import FilterModelMap from '@/utils/grid/datasource/FilterModelMap';
import { SELECT_FILTER_TYPE } from '@/utils/grid/filters/SelectFilter';
import AppConfig from '@/utils/app-config.mjs';
import { TransferListCustomParams } from './types';


interface TransferListMeta {
  hasMore: boolean;
  offset: number;
  resultCount: number;
}

export interface TransferList<T> {
  items: T[];
  meta: TransferListMeta;
}

const multiValueSeparator = ';';

/** Status/set filters use `all` to mean no restriction — omit from API filter[]. */
function valuesWithoutAll(values: unknown[] | undefined): string[] {
  return (values ?? []).filter((v): v is string => v !== 'all');
}

/**
 * Adapts the agGrid fetch interface to the Transfer List API.
 */
export default class QueueDataSource implements IDatasource {
  private hasLinkedTransferFilterBeenUsed = false;
  /**
   * Fetches transfer data from the API.
   */
  async fetchData({
    startRow,
    sortModel,
    showPendingTransfers,
    filterModel,
    isArchive,
    type,
  }: TransferListCustomParams) {
    const {searchText,sellerLicenseKey, ...restFilterModel} = filterModel
let linkedTransferValue: string | undefined = undefined;

    
    if (sellerLicenseKey?.values?.includes("linked_transfer")) {
      linkedTransferValue = "exclude_linked";
    }

    // Include sellerLicenseKey so FFL/Non FFL and license filters are passed to the API
    const filterModelForApi = { ...restFilterModel, ...(sellerLicenseKey && { sellerLicenseKey }) };

    // convert the request model to the API model

    const params = {
      startRow,
      pageSize: SERVER_PAGE_SIZE,
      orderBy: this.mapSortModel(sortModel),
      'filter[]': this.mapFilterModel(filterModelForApi, type).join(),
      ...(showPendingTransfers ? {showPendingTransfers: true}: {}),
      ...(isArchive ? {isArchive: true}: {}),
      ...(searchText && searchText.filter ? {search: searchText.filter}:{}),
      ...(linkedTransferValue && { linkedTransfers: linkedTransferValue }),
      ...this.getAgentFilter(restFilterModel),
    };

    const response = await client.get(
      `${AppConfig.fflExpressServiceApiURL}/backend/transfers/list`,
      { params }
    );
    return response.data as TransferList<Transfer>;
  }

  /**
   * Maps agGrid sort model to API sort parameters.
   */
  mapSortModel(sortModel: SortModelItem[]): string[] {
    return sortModel.map((s) => {
      if (s.colId === 'age') {
        return ['startDate', s.sort === 'asc' ? 'desc' : 'asc'].join(API_KEYWORD_SEPARATOR);
      }
      const field = SortModelMap.get(s.colId) || s.colId;
      return [field, s.sort].join(API_KEYWORD_SEPARATOR);
    });
  }

  /**
   * Extracts special filters (agent, archive, linked) handled as query params.
   */
  getAgentFilter(filterModel: FilterModel): Record<string, unknown> {
    const filters: Record<string, unknown> = {};
    
    Object.entries(filterModel).forEach(([key, value]) => {
      const field = FilterModelMap.get(key) || key;
      if (field === 'agent' && value.values.includes(ONLY_MY_TRANSFER_LABEL)) {
        filters.onlyMyTransfers = true;
      }
      if (field === 'isArchive' && value.values.includes('true')) {
        filters.isArchive = true;
      }
      if (field === 'linkedTransfers' && value.values.length > 0) {
        filters.linkedTransfers = value.values[0];
      }
    });
    
    return filters;
  }

  /**
   * Maps agGrid filter model to API filter parameters.
   */
  mapFilterModel(filterModel: FilterModel, type?: string): string[] {
    const filters: string[] = [];
    
    Object.entries(filterModel).forEach(([key, value]) => {
      const field = FilterModelMap.get(key) || key;
      let op;
      let val;

      // Skip filters handled separately
      if (field === 'agent' || field === 'isArchive' || field === 'linkedTransfers') return;

      // Handle seller license key filter
      if (field === 'sellerLicenseKey') {
        const hasFFL = value.values.includes('ffl');
        const hasNonFFL = value.values.includes('non_ffl');
       
        // Get actual license numbers (excluding ffl/non_ffl)
        const licenseNumbersLinkedTransfer = value.values.filter((v: any) => v !== 'ffl' && v !== 'non_ffl');
        const licenseNumbers = licenseNumbersLinkedTransfer.filter((v: any) => v !== 'linked_transfer');
        
        if (licenseNumbers.length > 0) {
          filters.push([field, 'inSet', licenseNumbers.join(multiValueSeparator)].join(API_KEYWORD_SEPARATOR));
        }
        if (hasFFL && !hasNonFFL) {
          filters.push([field, 'notBlank', ''].join(API_KEYWORD_SEPARATOR));
        } else if (!hasFFL && hasNonFFL) {
          filters.push([field, 'blank', ''].join(API_KEYWORD_SEPARATOR));
        }
        return;
      }

      switch (value.filterType) {
        case 'date':
          op = value.type;
          if (value.type === 'inRange') {
            val = [value.dateFrom, value.dateTo].join(multiValueSeparator);
          } else if (value.type !== 'blank' && value.type !== 'notBlank') {
            val = value.dateFrom;
          }
          break;
        case 'set': {
          const setValues = valuesWithoutAll(value.values);
          if (setValues.length === 0) {
            break;
          }
          op = 'inSet';
          val =
            field === 'firearm'
              ? setValues.map((v) => String(v).toLowerCase()).join(multiValueSeparator)
              : setValues.join(multiValueSeparator);
          break;
        }
        case 'text':
          op = value.type;
          val = value.filter.toLowerCase();
          break;
        case SELECT_FILTER_TYPE:
          op = 'equals';
          [val] = value.values;
          break;
        default:
          logger.error(`No mapFilterModel handler for filter type ${value.filterType}`);
      }

      if (type === 'notEqual' && (field === 'derivedStatus' || field === 'microStatus')) {
        const setValues = valuesWithoutAll(value.values);
        if (setValues.length === 0) {
          val = undefined;
        } else {
          op = 'notEqual';
          val = setValues.join(multiValueSeparator);
        }
      }

      if (val) {
        const fieldName = field === 'storeName' ? 'store_id' : field;
        filters.push([fieldName, op, val].join(API_KEYWORD_SEPARATOR));
      }
    });
    
    return filters;
  }

  /**
   * Get rows from the API
   * @param params
   */
  getRows(params: TransferListCustomParams): void {
    const pageNumber = (params.startRow / 30)  + 1;
    this.fetchData({...params, startRow: pageNumber})
      .then((result) => {
        params.successCallback(
          result.items
        );
      })
      .catch((error) => {
        logger.error('Error while fetching rows for ag-Grid', error);
        params.failCallback();
      });
  }
}

interface FilterModel {
  values: unknown[];
  filterType: string;
}
